<template>
  <div>
    <h5 class="card-title">
      <i class="fas fa-eye-slash"></i>
      Denúncias não visualizadas
    </h5>

    <div v-if="loading" class="text-center">
      <b-spinner small></b-spinner>
      <span class="ml-2">Carregando...</span>
    </div>
    <div v-else-if="data.length === 0" class="text-muted text-center">
      <i class="fas fa-check-circle"></i>
      Nenhuma denúncia não visualizada encontrada
    </div>
    <div v-else>
      <div class="mb-3">
        <div>
          <b-badge variant="warning" class="mr-2">
            Total: {{ totalUnviewed }}
          </b-badge>
          <small class="text-muted"
            >denúncias não visualizadas pelo cliente</small
          >
        </div>
      </div>

      <div class="complaints-list-container">
        <div class="complaints-header">
          <div class="row-content">
            <div class="protocol-section header-label">Protocolo</div>
            <div class="company-section header-label">Empresa</div>
            <div class="days-section header-label">Dias sem visualização</div>
            <div class="date-section header-label">Última movimentação</div>
          </div>
        </div>
        <div class="complaints-scroll" @scroll="handleScroll">
          <div class="complaints-list">
            <div
              v-for="complaint in visibleComplaints"
              :key="complaint._id"
              class="complaint-row"
              @click="viewComplaint(complaint)"
            >
              <div class="row-content">
                <div class="protocol-section">
                  <strong class="protocol-link">{{
                    complaint.protocol
                  }}</strong>
                </div>

                <div class="company-section">
                  <span class="company-name">{{ complaint.companyName }}</span>
                </div>

                <div class="days-section">
                  <b-badge variant="danger" class="days-badge">
                    <i class="fas fa-eye-slash mr-1"></i>
                    {{ Math.floor(complaint.daysSinceLastEvent) }} dias
                  </b-badge>
                </div>

                <div class="date-section">
                  <span class="date-text">{{
                    complaint.lastMovementDate | datetime
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getComplaintsWithViews } from "../../../../api/charts";

export default {
  name: "ComplaintsWithViews",
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      data: [],
      loading: true,
      limit: 15,
    };
  },
  computed: {
    // Achatar dados do backend em lista única
    allComplaints() {
      const complaints = [];
      this.data.forEach((company) => {
        company.complaints.forEach((complaint) => {
          complaints.push({
            ...complaint,
            companyName: company._id.fantasyName || company._id,
          });
        });
      });
      return complaints.sort(
        (a, b) => b.daysSinceLastEvent - a.daysSinceLastEvent
      );
    },
    // Retorna todas as denúncias, já que a busca foi removida
    filteredComplaints() {
      return this.allComplaints;
    },
    visibleComplaints() {
      return this.filteredComplaints.slice(0, this.limit);
    },
    totalUnviewed() {
      return this.allComplaints.length;
    },
  },
  watch: {
    companyId() {
      this.fetchData();
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;
        this.limit = 15;
        const response = await getComplaintsWithViews(this.companyId);
        this.data = response.data;
      } catch (error) {
        console.error("Erro ao carregar denúncias não visualizadas:", error);
        this.$bvToast.toast("Erro ao carregar denúncias não visualizadas", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    viewComplaint(complaint) {
      this.$router.push(`/denuncias/${complaint._id}`);
    },
    handleScroll(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        if (this.limit < this.filteredComplaints.length) {
          this.limit += 15;
        }
      }
    },
    getStatusText(status) {
      const statusMap = {
        0: "Nova",
        1: "Processando",
        2: "Concluída",
      };
      return statusMap[status] || "Desconhecido";
    },
    getStatusVariant(status) {
      const variantMap = {
        0: "warning",
        1: "primary",
        2: "success",
      };
      return variantMap[status] || "secondary";
    },
  },
};
</script>

<style scoped>
.complaints-list-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.complaints-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.header-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
}

.complaints-scroll {
  max-height: 460px; /* Ajustado para compensar o header */
  overflow-y: auto;
}

.complaints-list {
  padding: 0;
}

.complaint-row {
  border-bottom: 1px solid #f1f3f4;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.complaint-row:hover {
  background-color: #f8f9fa;
}

.complaint-row:last-child {
  border-bottom: none;
}

.row-content {
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 2fr;
  gap: 1rem;
  align-items: center;
}

.protocol-section {
  display: flex;
  align-items: center;
}

.protocol-link {
  color: #007bff;
  font-weight: 600;
  font-size: 0.95rem;
}

.company-section {
  display: flex;
  align-items: center;
}

.company-name {
  color: #6c757d;
  font-size: 0.9rem;
}

.days-section {
  display: flex;
  justify-content: center;
}

.days-badge {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.4rem 0.6rem;
  border-radius: 15px;
}

.date-section {
  display: flex;
  align-items: center;
}

.date-text {
  color: #6c757d;
  font-size: 0.85rem;
  white-space: nowrap;
}

.actions-section {
  display: flex;
  justify-content: center;
}

.action-icon {
  color: #007bff;
  font-size: 0.9rem;
}

.days-section.header-label {
  justify-content: center;
}

.actions-section.header-label {
  justify-content: center;
}

/* Scroll personalizado */
.complaints-scroll::-webkit-scrollbar {
  width: 6px;
}

.complaints-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.complaints-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.complaints-scroll::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsividade */
@media (max-width: 992px) {
  .row-content {
    grid-template-columns: 1.5fr 1.5fr 1.2fr 1.8fr;
    gap: 0.5rem;
  }

  .protocol-link,
  .company-name,
  .date-text {
    font-size: 0.85rem;
  }

  .days-badge {
    font-size: 0.75rem;
    padding: 0.3rem 0.5rem;
  }
}

@media (max-width: 768px) {
  .row-content {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 0.5rem;
  }

  .protocol-section {
    grid-column: 1 / 2;
  }

  .company-section {
    grid-column: 2 / 3;
  }

  .days-section {
    grid-column: 3 / 4;
    justify-self: end;
  }

  .date-section {
    grid-column: 1 / 4;
    grid-row: 2;
  }

  .complaint-row {
    padding: 0.6rem 0.8rem;
  }
}

@media (max-width: 576px) {
  .row-content {
    grid-template-columns: 1fr;
    gap: 0.3rem;
  }

  .protocol-section,
  .company-section,
  .days-section,
  .date-section,
  .actions-section {
    grid-column: 1;
    justify-self: start;
  }

  .days-section {
    justify-self: start;
  }

  .actions-section {
    display: none;
  }
}
</style>
